import React, {useCallback, useEffect, useMemo, useRef, useState} from "react";
import {FontAwesomeIcon} from "@fortawesome/react-fontawesome";
import {faPlus, faTriangleExclamation} from "@fortawesome/pro-solid-svg-icons";
import {
    faArrowLeft,
    faCloudArrowUp,
    faDownload,
    faPencil,
    faSave,
    faSquare,
    faXmark
} from "@fortawesome/pro-regular-svg-icons";
import {faLayerGroup} from "@fortawesome/pro-light-svg-icons";
import {faBuilding} from "@fortawesome/pro-thin-svg-icons";
import {toast} from "react-toastify";
import {useSearchParams} from "react-router-dom";

import {confirmAlert} from "react-confirm-alert";
import useProjectEditAccess from "@/services/context/project-process/use-project-edit-access";
import useProjectOrganisationContext
    from "../../../../context/project-organisation/use-project-organisation-context.ts";
import usePreventNavigationContext from "@/services/context/prohibit-nagivation/use-prevent-navigation-context.ts";
import useProjectFormNavigationContext
    from "@/pages/takeoff/context/project-form-navigation/use-project-form-navigation-context.ts";

import SimpleLoadingIndicator from "@/components/simple-loading-indicator/index.tsx";
import Card from "@/components/card";
import File2DList from "./components/file2d-list.tsx";
import File3DList from "./components/file3d-list.tsx";
import AddOrEditStoreyDialog from "./components/add-or-edit-storey-dialog.tsx";
import AddOrEditBuildingDialog from "./components/add-or-edit-building-dialog.tsx";
import ProjectEditButton from "@/components/buttons/project-edit-button";
import {useProjectFormHeadingLayoutEffect} from "../../../../context/project-heading/use-project-heading-context.ts";
import Button from "@/components/buttons/button";
import useProjectContext from "@/services/context/project/use-project-context.ts";
import FlowMode from "@/models/enums/flow-mode.ts";
import Walkthrough from "@/components/walktrough";
import TruncatedText from "@/components/text/truncated-text";
import ProjectOrganisation from "@/pages/takeoff/context/project-organisation/models/project-organisation.ts";
import BaseDialog from "@/components/dialogs/base-dialog";
import LoadingButton from "@/components/buttons/loading-button";
import {ProjectHeadingState} from "@/pages/takeoff/context/project-heading/project-heading-context-props.ts";

const ProjectOrganisationOverview: React.FC = () => {
    const { ensureCanEdit, canEdit } = useProjectEditAccess();
    const { project } = useProjectContext();

    const [searchParams] = useSearchParams();
    const showWalkthrough = (searchParams.get('showWalkthrough') === 'true');

    const { isLoading, data, isDirty, isSaving, removeBuilding, removeStorey, tryAddPendingFile, canSaveChanges, appendRoofs, saveChanges } = useProjectOrganisationContext();

    const { setRoute } = useProjectFormNavigationContext();

    const { setIsNavigationPrevented, setSaveCallback } = usePreventNavigationContext();

    useEffect(() => {
        setIsNavigationPrevented(isDirty || isSaving);
        return () => setIsNavigationPrevented(false);
    }, [isDirty, isSaving, setIsNavigationPrevented]);

    const buildingIdRef = useRef<string | undefined>(undefined);
    const storeyIdRef = useRef<string | undefined>(undefined);
    const [storeyDialogOpen, setStoreyDialogStoreyDialogOpen] = useState(false)
    const [buildingDialogOpen, setBuildingDialogOpen] = useState(false);
    const [uploadError, setUploadError] = useState<string | null>(null);
    const [isDragging, setIsDragging] = useState(false);

    const fileInputRef = useRef<HTMLInputElement>(null);
    const dragCounterRef = useRef<number>(0);
    
    const handleSave = useCallback(async (): Promise<boolean> => {
        if (!ensureCanEdit()) {
            return false;
        }

        if (!data) return false;

        const validationResult = canSaveChanges(data);
        if (!validationResult.success) {
            toast.error(validationResult.message);
            return false;
        }

        const save = async function (projectData: ProjectOrganisation): Promise<boolean> {
            try {
                await saveChanges(projectData);
                return true;
            }
            catch (error) {
                toast.error(error instanceof Error ? error.message : 'Une erreur est survenue lors de la sauvegarde.');
                return false;
            }
        };

        if (!data.buildings.find(b => b.storeys.find(s => s.storeyType === 'TOIT') == null)) {
            return save(data);
        }

        const shouldAppendRoofs = await new Promise<boolean>((resolve) => {
            confirmAlert({
                customUI: ({onClose}) => (
                    <BaseDialog open={true} onClose={() => {
                        onClose();
                        resolve(false);
                    }}>
                        <div className='flex flex-col gap-y-2 pt-1.5'>
                            <div className='flex flex-row gap-x-4 items-center'>
                                <FontAwesomeIcon icon={faTriangleExclamation} className='text-yellow-400 text-5xl'/>
                                <div className='flex flex-col gap-y-2'>
                                    <span className='text-sm'>
                                        Les toitures n'ont pas été créées dans certains bâtiments. Elles sont nécessaires pour calculer les surfaces et quantitatifs des derniers étages.
                                    </span>
                                    <span>Voulez-vous les rajouter automatiquement ?</span>
                                </div>
                            </div>

                            <div className='flex flex-row gap-x-2 ml-auto'>
                                <Button onClick={() => {
                                    onClose();
                                    resolve(true);
                                }}>Oui</Button>
                                <Button onClick={() => {
                                    onClose();
                                    resolve(false);
                                }}>Non</Button>
                            </div>
                        </div>
                    </BaseDialog>
                )
            });
        });

        if (!ensureCanEdit()) {
            return false;
        }

        const dataToSave = shouldAppendRoofs ? appendRoofs(data) : data;
        return save(dataToSave);
    }, [ensureCanEdit, data, appendRoofs, canSaveChanges, saveChanges]);

    useEffect(() => {
        setSaveCallback(handleSave);
        return () => {
            setSaveCallback(undefined);
        };
    }, [handleSave, setSaveCallback]);

    const heading = useMemo<ProjectHeadingState>(
        () => ({
            title: 'Organisation',
            left:
                <div className='flex flex-row gap-x-2'>
                    <Button variant='simple' className='flex flex-row gap-y-2 cursor-pointer' disabled={isSaving} onClick={() => setRoute('measures')}>
                        <FontAwesomeIcon icon={faArrowLeft} />
                    </Button>

                    {canEdit &&
                        <LoadingButton onClick={async () => handleSave()}
                                       isLoading={isSaving}
                                       size="sm"
                                       disabled={!isDirty || isSaving}
                                       body={<FontAwesomeIcon icon={faSave} className='text-sm'/>}
                        />
                    }
                </div>,
        }), [isSaving, canEdit, isDirty, setRoute, handleSave]);

    useProjectFormHeadingLayoutEffect(heading);

    if (isLoading)
        return <SimpleLoadingIndicator text={'Chargement des données d\'organisation...'} />

    if (data == null)
        return <>An error occurred</>

    const handleFileSelect = async (files: FileList | null) => {
        if (!files || files.length === 0) return;

        setUploadError(null);
        const firstFile = files[0];

        if (!firstFile.name.toLowerCase().endsWith('.pdf')) {
            setUploadError("Seuls les fichiers PDF sont acceptés");
            return;
        }

        try {
            Array.from(files).forEach((file) => {
                if (!file.name.toLowerCase().endsWith('.pdf')) return;
                const tempFileId = crypto.randomUUID();
                tryAddPendingFile(tempFileId, file, 'project');
            });
        } catch (error) {
            if (error instanceof Error)
                setUploadError(error.message);
        }

        if (fileInputRef.current) {
            fileInputRef.current.value = '';
        }
    };

    return (
        <>
            <Walkthrough
                isWalkthroughOpen={showWalkthrough}
                refreshKey={data.buildings.length} // Permet de remettre en position un popover si le nombre de bâtiments change. Clé suffisante pour ce cas.
                items={[
                    {
                        targetId: 'add-building-card',
                        content: <span>Créer un bâtiment puis des étages</span>,
                    },
                    {
                        targetId: 'file-drop-zone',
                        position: 'right',
                        content: <span>Glisser et déposer des plans pour ensuite les ajouter dans les étages</span>,
                    },
                ]} />

            <div className={`w-full overflow-y-auto flex relative`}
                onDragOver={(e) => {
                    e.preventDefault();
                    e.stopPropagation();
                }}
                onDragEnter={(e) => {
                    if (!e.dataTransfer.types.includes('Files')) {
                        return;
                    }

                    e.preventDefault();
                    e.stopPropagation();
                    dragCounterRef.current += 1;
                    if (!isDragging) setIsDragging(true);
                }}
                onDragLeave={(e) => {
                    if (!e.dataTransfer.types.includes('Files')) {
                        return;
                    }

                    e.preventDefault();
                    e.stopPropagation();
                    dragCounterRef.current = Math.max(0, dragCounterRef.current - 1);
                    if (dragCounterRef.current === 0) setIsDragging(false);
                }}
                onDrop={(e) => {
                    e.preventDefault();
                    e.stopPropagation();
                    dragCounterRef.current = 0;
                    setIsDragging(false);
                    handleFileSelect(e.dataTransfer.files);
                }}>

                <div className='flex flex-row w-full bg-[#e4ede9]'>
                    <input ref={fileInputRef} type="file" accept={'.pdf'} className="hidden"
                        onChange={(e) => handleFileSelect(e.target.files)} />
                    
                    <Card additionalClasses='w-70 my-4 ml-4' colors='bg-white border-gray-300 border-dashed border-2' id='file-drop-zone'>
                        <div className='relative flex flex-col h-full' >
                            <div className={`flex-1 p-2 ${data.files2d.length === 0 ? 'absolute inset-0' : ''}`}>
                                <File2DList itemsSource={data.files2d} scope={'project'} noDataText=''/>
                            </div>

                            {uploadError && <div className='relative text-red-500'>{uploadError}</div>}

                            <div className={`relative flex flex-col items-center justify-center p-6 text-center ${data.files2d.length === 0 ? 'flex-1 pointer-events-none' : ''}`}>
                                <div className="rounded-full bg-blue-50 flex items-center justify-center text-blue-500 size-14">
                                    <FontAwesomeIcon icon={faCloudArrowUp} className="text-2xl" />
                                </div>
                                <span className="text-sm font-medium text-gray-900 mt-2 mb-5">
                                Glissez et déposez vos plans PDF ici
                            </span>

                                <Button variant="primary" size="sm" onClick={() => fileInputRef.current?.click()} className="pointer-events-auto flex flex-row items-center gap-1.5">
                                    <FontAwesomeIcon icon={faPlus} className="text-xs" />
                                    <span>Ajouter des plans</span>
                                </Button>
                            </div>
                        </div>
                    </Card>

                    <div className='flex flex-row flex-1 overflow-x-auto gap-x-4 p-4'>
                        {data.buildings.map((building, buildingIndex) => (
                            <Card key={`building-${buildingIndex}`} additionalClasses='min-w-100 max-w-120 h-fit' additionalContentClasses='pt-1 pb-2'>
                                <div className='flex flex-row w-full p-2 border-b border-gray-300 '>
                                    <div className='flex flex-row items-center gap-x-1 flex-1 pb-1'>
                                        <FontAwesomeIcon icon={faBuilding} className='text-2xl' />
                                        <div className='flex flex-col gap-y-0.5'>
                                        <TruncatedText className='font-semibold' key={`item-${buildingIndex}`} text={building.name} />
                                            {project.flowMode === FlowMode._3D2D && (
                                                <div>
                                                    <div className='text-xs'>Fichiers 3D</div>
                                                    <File3DList itemsSource={building.files3d} />
                                                </div>
                                            )}
                                        </div>
                                    </div>

                                    <ProjectEditButton size='2xs' variant='ghost' onClick={() => {
                                        buildingIdRef.current = building.id;
                                        setBuildingDialogOpen(true);
                                    }}>
                                        <FontAwesomeIcon icon={faPencil} className='text-base' />
                                    </ProjectEditButton>

                                    <ProjectEditButton size='2xs' variant='ghost'
                                        onClick={() => {
                                            removeBuilding(building.id);
                                        }}>
                                        <FontAwesomeIcon icon={faXmark} className='text-red-500 text-lg' />
                                    </ProjectEditButton>
                                </div>

                                <div className='px-2 flex flex-col pt-2'>
                                    <div className='flex flex-row w-full items-center'>
                                        <div className='flex-1 flex flex-row items-center gap-x-1'>
                                            <FontAwesomeIcon icon={faLayerGroup} />
                                            <span className='text-base'>Niveaux</span>
                                        </div>

                                        <ProjectEditButton
                                            variant='ghost' size='xs'
                                            onClick={() => {
                                                buildingIdRef.current = building.id;
                                                storeyIdRef.current = undefined;
                                                setStoreyDialogStoreyDialogOpen(true);
                                            }}>
                                            <FontAwesomeIcon icon={faPlus} className='text-green-500' />
                                        </ProjectEditButton>
                                    </div>

                                    <div className='flex flex-col py-2 pl-6 pr-1'>
                                        {building.storeys.map((storey, storeyIndex) => (
                                            <div key={`storey-${storeyIndex}`} className='flex flex-col p-2 gap-y-0.5 border-l border-x border-t border-gray-300 last:border-b first:rounded-t-md last:rounded-b-md'>
                                                <div className='flex flex-row items-center gap-x-2'>
                                                    <FontAwesomeIcon icon={faSquare} className='rotate-x-60 rotate-z-45' />
                                                    <TruncatedText text={storey.name} className='flex-1 font-semibold'/>
                                                    <span className='text-sm text-gray-400'>{storey.altimetry} m</span>

                                                    <div className='flex flex-row'>
                                                        <ProjectEditButton variant='ghost' size='2xs' onClick={() => {
                                                            buildingIdRef.current = building.id;
                                                            storeyIdRef.current = storey.id;
                                                            setStoreyDialogStoreyDialogOpen(true);
                                                        }}>
                                                            <FontAwesomeIcon icon={faPencil} />
                                                        </ProjectEditButton>

                                                        <ProjectEditButton variant='ghost' size='2xs'
                                                                             onClick={() => {
                                                                                 removeStorey(building.id, storey.id);
                                                                             }}>
                                                            <FontAwesomeIcon icon={faXmark} className='text-red-500' />
                                                        </ProjectEditButton>
                                                    </div>

                                                </div>
                                                <div className='pl-8'>
                                                    <File2DList itemsSource={storey.files2d} scope={'storey'} buildingId={building.id} storeyId={storey.id} />
                                                </div>
                                            </div>
                                        ))}
                                    </div>
                                </div>
                            </Card>
                        ))}

                        <Card additionalClasses='h-fit' id='add-building-card' >
                            <ProjectEditButton
                                variant='ghost' size='2xs'
                                onClick={() => {
                                    buildingIdRef.current = undefined;
                                    setBuildingDialogOpen(true);
                                }}>

                                <div className='py-2 pl-2 pr-4 flex flex-row gap-x-4 items-center text-base'>
                                    <FontAwesomeIcon icon={faPlus} className='text-green-500'/>
                                    <div className='flex-1'>Nouveau bâtiment</div>
                                </div>
                            </ProjectEditButton>
                        </Card>
                    </div>
                </div>

                <div className={`absolute inset-4 flex border-2 border-dashed rounded-lg border-blue-500 bg-blue-300 pointer-events-none ${isDragging ? 'opacity-80' : 'opacity-0'}`}>
                    <div className='m-auto flex flex-row gap-x-8 text-white items-center'>
                        <FontAwesomeIcon icon={faDownload} className='text-3xl' />
                        <div className='text-xl'>Déposer votre fichier</div>
                    </div>
                </div>

            </div>

            <AddOrEditBuildingDialog buildingId={buildingIdRef.current} open={buildingDialogOpen} onClose={() => setBuildingDialogOpen(false)} />
            <AddOrEditStoreyDialog buildingId={buildingIdRef.current} storeyId={storeyIdRef.current} open={storeyDialogOpen} onClose={() => setStoreyDialogStoreyDialogOpen(false)} />
        </>
    )
}

export default ProjectOrganisationOverview;
